
import { toast } from "@/hooks/use-toast";
import { geocodeAddress, type Location } from "@/utils/mapUtils";

export const handleLocationSearch = (
  searchInput: string,
  setIsSearching: (value: boolean) => void,
  onLocationFound: (location: Location) => void,
  clearInput: () => void
) => {
  if (!searchInput.trim()) {
    toast({
      title: "Search Error",
      description: "Please enter a location to search for",
      variant: "destructive",
    });
    return;
  }
  
  setIsSearching(true);
  
  // Check if coordinates were entered directly
  const coordsRegex = /^\s*(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)\s*$/;
  const match = searchInput.match(coordsRegex);
  
  if (match) {
    try {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[3]);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        // Use setTimeout to ensure UI updates before processing the result
        setTimeout(() => {
          setIsSearching(false);
          onLocationFound({
            lat, 
            lng,
            name: `Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`
          });
          
          toast({
            title: "Location Found",
            description: `Setting location to coordinates: (${lat.toFixed(4)}, ${lng.toFixed(4)})`,
          });
          clearInput();
        }, 10);
        return;
      }
    } catch (e) {
      console.error("Error parsing coordinates:", e);
    }
  }
  
  // Set a timeout to prevent hanging in case of API issues
  const timeoutId = setTimeout(() => {
    setIsSearching(false);
    toast({
      title: "Search Timeout",
      description: "The search is taking too long. Please try a more specific location or use the map directly.",
      variant: "destructive",
    });
  }, 10000); // 10-second timeout
  
  // If not coordinates, use geocoding service with faster timeouts
  geocodeAddress(
    searchInput,
    (location) => {
      clearTimeout(timeoutId);
      setIsSearching(false);
      onLocationFound(location);
      clearInput();
      
      toast({
        title: "Location Found",
        description: `Found location: ${location.name || "Unknown location"}`,
      });
    },
    (errorMessage) => {
      clearTimeout(timeoutId);
      setIsSearching(false);
      toast({
        title: "Location Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  );
};
